import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "sz_consent_analytics";

/**
 * Cookie consent banner (KVKK / GDPR aware).
 * Analytics + PostHog session tracking is initialised ONLY after explicit consent.
 * Essential application functionality works regardless of choice.
 */
export function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false); // triggers the fade + slide-up

  useEffect(() => {
    try {
      const choice = localStorage.getItem(STORAGE_KEY);
      if (choice !== "granted" && choice !== "denied") {
        // Mount hidden first, then flip `entered` on the next frame so the
        // transition classes actually animate (from → to state).
        const t = setTimeout(() => {
          setVisible(true);
          // wait one paint before triggering the transition
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setEntered(true));
          });
        }, 400);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage unavailable — silently skip */
    }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "granted");
    } catch { /* ignore */ }
    // Upgrade GA4 consent (Consent Mode v2) — gtag.js already loaded in <head>
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
      });
    }
    // Boot PostHog only after explicit consent
    if (typeof window !== "undefined" && typeof window.__szInitPosthog === "function") {
      window.__szInitPosthog();
    }
    // Boot Microsoft Clarity only after explicit consent
    if (typeof window !== "undefined" && typeof window.__szInitClarity === "function") {
      window.__szInitClarity();
    }
    // Boot Meta Pixel only after explicit consent (uses pixel id already
    // fetched from /api/config by initAnalytics()).
    if (typeof window !== "undefined" && typeof window.__szInitMetaPixel === "function") {
      window.__szInitMetaPixel();
    }
    dismiss();
  };

  const decline = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "denied");
    } catch { /* ignore */ }
    // Explicitly reaffirm GA4 denial (defensive — default is already denied)
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
      });
    }
    dismiss();
  };

  // Fade + slide-down out, then unmount so the transition is visible.
  const dismiss = () => {
    setEntered(false);
    setTimeout(() => setVisible(false), 260);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Çerez tercihleri"
      data-testid="consent-banner"
      className={`fixed z-50 top-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:top-auto sm:left-auto sm:right-6 sm:bottom-6 sm:translate-x-0 sm:w-auto max-w-md rounded-2xl border border-zinc-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.12)] p-5 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
        entered
          ? "opacity-100 translate-y-0 sm:translate-y-0"
          : "opacity-0 -translate-y-2 sm:translate-y-4"
      }`}
    >
      <div className="text-sm font-semibold text-zinc-900">Çerez tercihleri</div>
      <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
        SuperZeka&apos;yı daha iyi hale getirmek için kullanım istatistikleri toplamak isteriz.
        Bu tercih tamamen sana ait — reddetsen bile SuperZeka&apos;yı tam kullanabilirsin.
        Detay için{" "}
        <Link to="/gizlilik" className="underline text-[#1E3A8A]" data-testid="consent-privacy-link">
          Gizlilik Politikası
        </Link>.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <button
          onClick={decline}
          data-testid="consent-decline"
          className="rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
        >
          Sadece gerekli olanlar
        </button>
        <button
          onClick={accept}
          data-testid="consent-accept"
          className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white px-4 py-2 text-sm font-semibold transition-colors"
        >
          Tümünü kabul et
        </button>
      </div>
    </div>
  );
}
